<template>
  <q-dialog v-model="showDialog" persistent>
    <q-card class="phone-verification-card">
      <q-card-section class="text-center q-pb-md q-pt-lg">
        <div v-if="!persistent" style="position: absolute; left: 16px; top: 16px;">
          <q-btn icon="close" flat round dense @click="onCancel" />
        </div>
        
        <q-icon name="verified_user" size="64px" color="primary" class="q-mb-md" />
        
        <div class="text-h5 text-weight-medium q-mb-sm">تأیید شماره تلفن</div>
        <p class="text-body2" style="line-height: 1.6; font-weight: 200;">
         حساب کاربری فعلی شما با این شماره تلفن <span style="color: red ; font-weight: 500;">ادغام</span> می‌شود و تمام گفت‌وگوها و اشتراک‌های شما به این شماره منتقل خواهد شد.
        </p>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <q-form @submit="onSubmit" class="q-gutter-md">
          <q-input
            v-model="phoneNumber"
            filled
            label="شماره موبایل"
            :maxlength="11"
            inputmode="numeric"
            :rules="[
              val => !!val || 'شماره موبایل الزامی است',
              val => {
                const englishVal = persianToEnglish ? persianToEnglish(val) : val;
                return /^09\d{9}$/.test(englishVal) || 'شماره موبایل معتبر نیست';
              }
            ]"
            lazy-rules
            :disable="loading"
            class="modern-input"
            style="direction: ltr !important;"
            hide-bottom-space
            no-error-icon
            @keypress="onlyNumbers"
          >
            <template v-slot:prepend>
              <q-icon name="phone" />
            </template>
          </q-input>

          <div class="button-container q-mt-md">
            <q-btn
              v-if="!persistent"
              label="انصراف"
              flat
              @click="onCancel"
              :disable="loading"
              class="cancel-btn"
            />
            <q-btn
              label="ارسال کد تأیید"
              type="submit"
              unelevated
              :loading="loading"
              :disable="loading"
              class="modern-button submit-btn"
            />
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref, getCurrentInstance } from 'vue';
import { useAuthStore } from 'src/stores/auth';
import { useQuasar } from 'quasar';

const appContext = getCurrentInstance();
const persianToEnglish = appContext?.appContext.config.globalProperties.$persianToEnglish;

interface Props {
  modelValue: boolean;
  persistent?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'verified', data: { phoneNumber: string }): void;
  (e: 'cancel'): void;
}

const props = withDefaults(defineProps<Props>(), {
  persistent: false
});

const emit = defineEmits<Emits>();

const authStore = useAuthStore();
const $q = useQuasar();

const showDialog = ref(props.modelValue);
const phoneNumber = ref('');
const loading = ref(false);

// Watch for external changes to modelValue
watch(() => props.modelValue, (newVal) => {
  showDialog.value = newVal;
});

// Watch for internal changes to showDialog
watch(showDialog, (newVal) => {
  emit('update:modelValue', newVal);
});

function onlyNumbers(event: KeyboardEvent) {
  const key = event.key;
  // Allow both English (0-9) and Persian (۰-۹) numbers
  if (!key.match(/[0-9۰-۹]/)) {
    event.preventDefault();
  }
}

async function onSubmit() {
  // Convert Persian digits to English
  const englishPhoneNumber = persianToEnglish ? persianToEnglish(phoneNumber.value) : phoneNumber.value;
  
  if (!englishPhoneNumber || !/^09\d{9}$/.test(englishPhoneNumber)) {
    $q.notify({
      type: 'negative',
      message: 'لطفاً شماره موبایل معتبر وارد کنید',
      position: 'top'
    });
    return;
  }

  loading.value = true;

  try {
    const response = await authStore.$api.post('initiate_phone_verification', {
      phone_number: englishPhoneNumber,
      is_app: false
    });

    if (response.data.status === 'ok') {
      // Move to OTP verification step - emit English digits for consistency
      emit('verified', { phoneNumber: englishPhoneNumber });
      showDialog.value = false;
    } else {
      $q.notify({
        type: 'negative',
        message: response.data.message || 'خطایی رخ داده است',
        position: 'top'
      });
    }
  } catch (error: any) {
    console.error('Phone verification error:', error);
    $q.notify({
      type: 'negative',
      message: error.response?.data?.message || 'خطا در ارسال کد تأیید',
      position: 'top'
    });
  } finally {
    loading.value = false;
  }
}

function onCancel() {
  if (!props.persistent) {
    emit('cancel');
    showDialog.value = false;
  }
}
</script>

<script lang="ts">
import { watch } from 'vue';
export default {
  name: 'PhoneVerificationDialog'
};
</script>

<style lang="scss" scoped>
.phone-verification-card {
  min-width: 350px;
  max-width: 450px;
  border-radius: 16px !important;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.modern-input {
  :deep(.q-field__control) {
    border-radius: 12px;
    height: 56px;

    &:before {
      border: none;
    }

    &:after {
      display: none;
    }
  }

  :deep(.q-field__native) {
    font-size: 1.1em;
    padding-right: 12px;
    line-height: normal;
    padding-top: 0;
    padding-bottom: 0;
    margin-top: auto;
    margin-bottom: auto;
  }

  :deep(.q-field__label) {
    top: 18px;
  }

  &:deep(.q-field--float .q-field__label) {
    display: none;
  }
}

.modern-button {
  border-radius: 12px;
  height: 56px;
  font-size: 1.1em;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-weight: 500;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
  }
}

.button-container {
  display: flex;
  gap: 12px;
  
  .cancel-btn {
    border-radius: 12px;
    height: 56px;
    border: 1px solid rgba(255, 255, 255, 0.15);
  }
  
  .submit-btn {
    flex: 1;
    color: var(--inverted-text);
    background-color: var(--inverted-main);
  }
}
</style>
